import { useState, type ReactElement } from "react";
import { Sparkles } from "lucide-react";
import type { Task, TaskBucket } from "@playbookai/types";
import { cn } from "../../lib/utils.js";
import { SegmentedControl } from "../../atoms/SegmentedControl/SegmentedControl.js";
import type { WidgetBodyProps } from "../types.js";
import type { MyTasksView } from "./types.js";

const BUCKET_ORDER: readonly TaskBucket[] = ["today", "later"];
const BUCKET_LABEL: Record<TaskBucket, string> = {
  today: "Do today",
  later: "Can wait",
};
const BUCKET_TONE: Record<TaskBucket, string> = {
  today: "text-pb-amber-text border-pb-amber/40 bg-pb-amber-soft",
  later: "text-pb-text-muted border-pb-border bg-pb-card-elevated",
};

function TaskRow({ task, why }: { task: Task; why: boolean }): ReactElement {
  return (
    <li className="flex items-start gap-2 py-1.5">
      {/* Kept hand-built: a decorative open task circle, not a control (completion is not wired here). */}
      <span className="w-4 h-4 mt-0.5 rounded-full border border-pb-text-muted shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-pb-text leading-snug">
          {task.title}
        </span>
        {why ? (
          <span className="flex items-start gap-1 mt-0.5">
            <Sparkles className="w-3 h-3 text-pb-iris-text mt-0.5 shrink-0" />
            <span className="text-dense-1 text-pb-text-secondary leading-snug">
              {task.why}
            </span>
          </span>
        ) : null}
        <span
          className={cn(
            "block text-dense-1 mt-0.5",
            task.urgent ? "text-pb-amber-text" : "text-pb-text-muted",
          )}
        >
          Due {task.due}
        </span>
      </span>
    </li>
  );
}

/**
 * My Tasks — dashboard data widget. An AI-ranked personal inbox grouped into
 * Do-today / Can-wait buckets, or a flat list. The in-widget AI/List toggle is
 * ephemeral local view state seeded from the saved `data.mode` (the persisted
 * default rides the params via the editor); `data.priorities` filters buckets.
 * Data-blind: the host resolves the task list. Completion is not wired here.
 */
export function MyTasksWidget({
  data,
}: WidgetBodyProps<MyTasksView>): ReactElement {
  const [override, setOverride] = useState<"ai" | "flat" | null>(null);
  const mode = override ?? data.mode;
  const shown = new Set(data.priorities);
  const visible = data.tasks.filter((t) => shown.has(t.bucket));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-dense-1 text-pb-text-muted">
          <Sparkles className="w-3 h-3 text-pb-iris-text" />
          {mode === "ai" ? "Ranked by PlayerAI" : "Flat list"}
        </span>
        <SegmentedControl<"ai" | "flat">
          size="sm"
          aria-label="Task ordering"
          value={mode}
          onChange={setOverride}
          options={[
            {
              value: "ai",
              label: "AI",
              icon: <Sparkles className="w-3 h-3 text-pb-iris-text" />,
            },
            { value: "flat", label: "List" },
          ]}
        />
      </div>

      {visible.length === 0 ? (
        <p className="text-dense-1 text-pb-text-muted italic">
          No tasks in view — you're all caught up.
        </p>
      ) : mode === "ai" ? (
        <div className="space-y-3">
          {BUCKET_ORDER.filter((b) => shown.has(b)).map((bucket) => {
            const rows = visible.filter((t) => t.bucket === bucket);
            if (rows.length === 0) return null;
            return (
              <div key={bucket}>
                <span
                  className={cn(
                    "inline-flex items-center text-dense-2 uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border mb-1.5",
                    BUCKET_TONE[bucket],
                  )}
                >
                  {BUCKET_LABEL[bucket]} · {rows.length}
                </span>
                <ul className="space-y-1.5">
                  {rows.map((t) => (
                    <TaskRow key={t.id} task={t} why />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map((t) => (
            <TaskRow key={t.id} task={t} why={false} />
          ))}
        </ul>
      )}
    </div>
  );
}
